import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { dashboardApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PAY_COLORS = {
    cash: '#E87722',
    transfer: '#3b82f6',
    card: '#10b981',
    pos: '#8b5cf6',
    other: '#94a3b8',
};
const FALLBACK_COLORS = ['#E87722', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

function fmt(v) {
    return '₦' + Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function pctInfo(val) {
    const n = Number(val || 0);
    return { abs: Math.abs(n), up: n >= 0 };
}

function todayLabel() {
    return new Date().toLocaleDateString('en-NG', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function shortTime(iso) {
    return new Date(iso).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, change, accentColor, bgColor, alert }) {
    return (
        <div className={`kpi-card${alert ? ' kpi-alert' : ''}`}
            style={{ '--kpi-accent': accentColor, '--kpi-bg': bgColor }}>
            <div className="kpi-icon-wrap">
                <span className="kpi-icon">{icon}</span>
            </div>
            <div className="kpi-body">
                <p className="kpi-label">{label}</p>
                <h3 className="kpi-value">{value}</h3>
                {change != null && (
                    <span className={`kpi-change ${change.up ? 'kpi-up' : 'kpi-down'}`}>
                        {change.up ? '↑' : '↓'} {change.abs}% vs yesterday
                    </span>
                )}
            </div>
        </div>
    );
}

// ── Custom PieChart tooltip ───────────────────────────────────────────────────
function PayTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="custom-tooltip">
            <strong>{p.method}</strong>
            <div>{fmt(p.amount)}</div>
            <div style={{ color: '#94a3b8' }}>{p.count} txn{p.count !== 1 ? 's' : ''}</div>
        </div>
    );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function DashboardPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const res = await dashboardApi.stats();
        if (res.success) {
            setData(res.data);
        } else {
            setError(res.error || 'Failed to load dashboard data.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="dash-loading">
                <div className="dash-spinner" />
                <p>Loading dashboard…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dash-error">
                <span>⚠️</span>
                <p>{error}</p>
                <button className="btn" onClick={load}>Retry</button>
            </div>
        );
    }

    const d = data || {};
    const today = d.today || {};
    const changes = d.changes || {};
    const weekChart = d.weekly_chart || [];
    const payBreak = d.payment_breakdown || [];
    const lowStock = d.low_stock_products || [];
    const recentTx = d.recent_transactions || [];
    const topProds = d.top_products || [];

    return (
        <div className="dashboard">

            {/* ── Welcome Banner ── */}
            <div className="dash-banner">
                <div className="dash-banner-left">
                    <p className="dash-greeting">{greeting()},</p>
                    <h2 className="dash-name">{user?.first_name || 'Admin'}</h2>
                    <p className="dash-sub">Here's what's happening with your store today.</p>
                </div>
                <div className="dash-banner-right">
                    <div className="dash-date-pill">{todayLabel()}</div>
                    {(user?.role === 'admin' || user?.role === 'manager') ? <Link className="btn btn-outline" to="/admin/settings">App Settings</Link> : null}
                    <button className="btn dash-refresh-btn" onClick={load} title="Refresh">↺ Refresh</button>
                </div>
            </div>

            {/* ── KPI Row ── */}
            <div className="kpi-grid">
                <KpiCard
                    label="Today's Sales"
                    value={fmt(today.total_sales)}
                    icon="₦"
                    change={pctInfo(changes.sales_pct)}
                    accentColor="#E87722"
                    bgColor="#fff5ec"
                />
                <KpiCard
                    label="Transactions"
                    value={today.transaction_count ?? 0}
                    icon="TX"
                    change={pctInfo(changes.transactions_pct)}
                    accentColor="#3b82f6"
                    bgColor="#eff6ff"
                />
                <KpiCard
                    label="Avg. Order"
                    value={fmt(today.avg_order)}
                    icon="AVG"
                    change={null}
                    accentColor="#10b981"
                    bgColor="#ecfdf5"
                />
                <KpiCard
                    label="Low Stock Alerts"
                    value={d.low_stock_count ?? 0}
                    icon="LOW"
                    change={null}
                    accentColor={d.low_stock_count > 0 ? '#dc2626' : '#10b981'}
                    bgColor={d.low_stock_count > 0 ? '#fff1f2' : '#ecfdf5'}
                    alert={d.low_stock_count > 0}
                />
            </div>

            {/* ── Charts Row ── */}
            <div className="dash-charts-row">

                {/* Sales Area Chart */}
                <div className="dash-panel dash-chart-main">
                    <div className="panel-header">
                        <div>
                            <h3>Sales Overview</h3>
                            <p className="panel-sub">Last 7 days performance</p>
                        </div>
                        <span className="chart-total">{fmt(today.total_sales)} <small>today</small></span>
                    </div>
                    {weekChart.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={weekChart} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#E87722" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#E87722" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis
                                    tickFormatter={(v) => v >= 1000 ? `₦${(v / 1000).toFixed(0)}k` : `₦${v}`}
                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                    width={58}
                                />
                                <Tooltip formatter={(v) => [fmt(v), 'Sales']} />
                                <Area
                                    type="monotone"
                                    dataKey="sales"
                                    stroke="#E87722"
                                    strokeWidth={2.5}
                                    fill="url(#salesGrad)"
                                    name="Sales"
                                    dot={{ r: 3, fill: '#E87722' }}
                                    activeDot={{ r: 5 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="dash-empty-chart">No sales data for the last 7 days</div>
                    )}
                </div>

                {/* Payment Breakdown */}
                <div className="dash-panel dash-chart-side">
                    <div className="panel-header">
                        <div>
                            <h3>Payment Methods</h3>
                            <p className="panel-sub">Today's breakdown</p>
                        </div>
                    </div>
                    {payBreak.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie
                                        data={payBreak}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={52}
                                        outerRadius={76}
                                        dataKey="amount"
                                        nameKey="method"
                                        paddingAngle={3}
                                        strokeWidth={0}
                                    >
                                        {payBreak.map((entry, i) => (
                                            <Cell
                                                key={i}
                                                fill={PAY_COLORS[entry.method] || FALLBACK_COLORS[i % FALLBACK_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<PayTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="pay-legend">
                                {payBreak.map((p, i) => (
                                    <div key={i} className="pay-legend-row">
                                        <span
                                            className="pay-dot"
                                            style={{ background: PAY_COLORS[p.method] || FALLBACK_COLORS[i] }}
                                        />
                                        <span className="pay-method">{p.method}</span>
                                        <span className="pay-pct">{p.percentage}%</span>
                                        <span className="pay-amt">{fmt(p.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="dash-empty-chart">No sales today yet</div>
                    )}
                </div>
            </div>

            {/* ── Middle Row: Recent Transactions + Top Products ── */}
            <div className="dash-bottom-row">

                {/* Recent Transactions */}
                <div className="dash-panel dash-panel-wide">
                    <div className="panel-header">
                        <h3>Recent Transactions</h3>
                        <Link to="/admin/pos" className="dash-link">POS →</Link>
                    </div>
                    <div className="table-wrap" style={{ marginTop: 0 }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Items</th>
                                    <th>Payment</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentTx.length === 0 ? (
                                    <tr><td colSpan={6} className="dash-empty-row">No transactions yet</td></tr>
                                ) : recentTx.map((t) => (
                                    <tr key={t.id}>
                                        <td><code className="tx-code">#{String(t.id).padStart(4, '0')}</code></td>
                                        <td>{t.item_count} item{t.item_count !== 1 ? 's' : ''}</td>
                                        <td>
                                            <span className={`badge-pay badge-pay-${t.payment_method}`}>
                                                {t.payment_method || '—'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge-type ${t.transaction_type === 'refund' ? 'badge-refund' : 'badge-sale'}`}>
                                                {t.transaction_type}
                                            </span>
                                        </td>
                                        <td className="tx-amount">{fmt(t.total_amount)}</td>
                                        <td className="tx-time">{shortTime(t.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Top Products */}
                <div className="dash-panel dash-panel-narrow">
                    <div className="panel-header">
                        <h3>Top Products</h3>
                        <span className="panel-sub">7-day</span>
                    </div>
                    {topProds.length === 0 ? (
                        <p className="dash-empty-chart">No product data yet</p>
                    ) : topProds.map((p, i) => (
                        <div key={i} className="top-prod-row">
                            <span className="top-prod-rank">{i + 1}</span>
                            <div className="top-prod-info">
                                <span className="top-prod-name">{p.name}</span>
                                <span className="top-prod-sold">{p.total_sold} sold</span>
                            </div>
                            <span className="top-prod-rev">{fmt(p.revenue)}</span>
                        </div>
                    ))}
                    <div className="top-prod-total">
                        <span>Total Revenue</span>
                        <strong>{fmt(topProds.reduce((s, p) => s + p.revenue, 0))}</strong>
                    </div>
                </div>
            </div>

            {/* ── Low Stock Alerts ── */}
            {lowStock.length > 0 && (
                <div className="dash-panel">
                    <div className="panel-header">
                        <h3>⚠️ Low Stock Alerts</h3>
                        <span className="badge-danger-pill">{lowStock.length} items need restocking</span>
                    </div>
                    <div className="low-stock-grid">
                        {lowStock.map((p) => {
                            const pct = Math.min(100, Math.round((p.quantity_in_stock / p.reorder_level) * 100));
                            const color = pct <= 25 ? '#dc2626' : pct <= 60 ? '#f59e0b' : '#10b981';
                            return (
                                <div key={p.id} className="ls-card">
                                    <div className="ls-name">{p.name}</div>
                                    <div className="ls-meta">
                                        <code className="ls-sku">{p.sku}</code>
                                        <span className="ls-cat">{p.category}</span>
                                    </div>
                                    <div className="ls-stock-row">
                                        <div className="ls-progress-track">
                                            <div
                                                className="ls-progress-fill"
                                                style={{ width: `${pct}%`, background: color }}
                                            />
                                        </div>
                                        <span className="ls-qty" style={{ color }}>
                                            {p.quantity_in_stock} <span className="ls-of">/ {p.reorder_level}</span>
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Summary Footer Row ── */}
            <div className="dash-summary-row">
                <div className="dash-summary-item">
                    <span className="ds-icon">💵</span>
                    <div>
                        <p className="ds-label">Cash Sales</p>
                        <p className="ds-val">{fmt(today.cash_sales)}</p>
                    </div>
                </div>
                <div className="dash-summary-item">
                    <span className="ds-icon">🏦</span>
                    <div>
                        <p className="ds-label">Transfer</p>
                        <p className="ds-val">{fmt(today.transfer_sales)}</p>
                    </div>
                </div>
                <div className="dash-summary-item">
                    <span className="ds-icon">💳</span>
                    <div>
                        <p className="ds-label">Card</p>
                        <p className="ds-val">{fmt(today.card_sales)}</p>
                    </div>
                </div>
                <div className="dash-summary-item">
                    <span className="ds-icon">🏷️</span>
                    <div>
                        <p className="ds-label">Total Discount</p>
                        <p className="ds-val">{fmt(today.total_discount)}</p>
                    </div>
                </div>
                <div className="dash-summary-item">
                    <span className="ds-icon">📦</span>
                    <div>
                        <p className="ds-label">Products</p>
                        <p className="ds-val">{d.total_products ?? 0} active</p>
                    </div>
                </div>
            </div>

        </div>
    );
}
